'use client'

// Shared marketing-surface UI — design tokens, chrome, and motion used by the
// public landing page (app/MarketingHomePage.tsx) and /features. Keep all
// marketing styling here so the two pages can't drift apart.

import { useState, useEffect } from 'react'
import Link from 'next/link'
import type { ReactNode, RefObject } from 'react'
import { MK } from './marketing-tokens'

// ─── Feature flags ───────────────────────────────────────────────────────────
// Pricing is hidden during the beta (nav + footer links + homepage section).
// Flip to `true` to restore — see docs/BETA_FLAGS.md.
export const SHOW_PRICING = false

// under .mk to avoid leaking into the app.
export const MARKETING_CSS = `
.mk {
  --mk-bg: ${MK.bg};
  --mk-surface: ${MK.surface};
  --mk-ink: ${MK.ink};
  --mk-body: ${MK.body};
  --mk-faint: ${MK.faint}; /* ≥4.5:1 on cream — small text uses this */
  --mk-line: ${MK.line};
  --mk-line-soft: ${MK.lineSoft};
  --mk-green: ${MK.green};
  --mk-green-deep: ${MK.greenDeep};
  --mk-green-ink: ${MK.greenInk};
  --mk-green-soft: ${MK.greenSoft};
  --mk-green-border: ${MK.greenBorder};
  --mk-green-text: ${MK.greenText};
  --mk-green-pale: ${MK.greenPale};
  --mk-error: ${MK.error};
  --mk-display: var(--font-display, Georgia, 'Times New Roman', serif);
  --mk-ease-out: cubic-bezier(0.25, 1, 0.5, 1);
  --mk-ease-expo: cubic-bezier(0.16, 1, 0.3, 1);
  background-color: var(--mk-bg);
  color: var(--mk-ink);
  font-family: var(--font-geist-sans, system-ui, sans-serif);
  -webkit-font-smoothing: antialiased;
}
.mk * { box-sizing: border-box; }
.mk ::selection { background: #D7E6DE; }

.mk-container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

/* ── Typography ── */
.mk-display {
  font-family: var(--mk-display);
  font-weight: 400; /* Instrument Serif ships a single weight */
  color: var(--mk-ink);
  letter-spacing: -0.01em;
  line-height: 1.1;
  margin: 0;
  text-wrap: balance;
}
.mk-display em {
  font-style: italic;
  font-weight: 400;
  color: var(--mk-green);
}
.mk-h2 { font-size: clamp(30px, 3.6vw, 44px); margin-bottom: 18px; }
.mk-h3 { font-size: clamp(26px, 3vw, 36px); margin-bottom: 18px; }
.mk-eyebrow {
  display: flex; align-items: center; gap: 10px;
  font-size: 12px; font-weight: 650; color: var(--mk-green);
  letter-spacing: 0.12em; text-transform: uppercase;
  margin: 0 0 14px;
}
.mk-eyebrow::before {
  content: ''; width: 28px; height: 0;
  border-top: 2px dashed var(--mk-green);
  opacity: 0.55;
}
.mk-eyebrow--center { justify-content: center; }
.mk-eyebrow--center::after {
  content: ''; width: 28px; height: 0;
  border-top: 2px dashed var(--mk-green);
  opacity: 0.55;
}
.mk-lead { font-size: 17px; color: var(--mk-body); line-height: 1.7; margin: 0; }

/* ── Sections ── */
.mk-section { padding: 96px 24px; background: var(--mk-bg); }
.mk-section--white { background: var(--mk-surface); }
.mk-section--dark { background: var(--mk-green-ink); }
.mk-stitch-top { border-top: 1px dashed var(--mk-line); }
#product, #beta, #faq, #newsletter { scroll-margin-top: 84px; }

/* ── Motion: stitch seams sew themselves in on scroll (JS-enhanced; static without JS) ── */
.mk-js .mk-stitch-top { border-top-color: transparent; position: relative; }
.mk-js .mk-stitch-top::before {
  content: ''; position: absolute; top: -1px; left: 0; right: 0; height: 1px;
  background-image: linear-gradient(90deg, var(--mk-line) 60%, transparent 0);
  background-size: 9px 1px;
  clip-path: inset(0 100% 0 0);
  transition: clip-path 1.1s var(--mk-ease-expo);
}
.mk-js .mk-stitch-top.is-in::before { clip-path: inset(0 0 0 0); }

/* ── Motion: hero load choreography ── */
@keyframes mk-rise {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: none; }
}
.mk-hero-seq > * { animation: mk-rise 0.65s var(--mk-ease-out) both; }
.mk-hero-seq > *:nth-child(1) { animation-delay: 0.05s; }
.mk-hero-seq > *:nth-child(2) { animation-delay: 0.14s; }
.mk-hero-seq > *:nth-child(3) { animation-delay: 0.22s; }
.mk-hero-seq > *:nth-child(4) { animation-delay: 0.34s; }
.mk-hero-seq > *:nth-child(5) { animation-delay: 0.44s; }
.mk-hero-seq > *:nth-child(6) { animation-delay: 0.52s; }
.mk-hero-frame { animation: mk-rise 0.85s var(--mk-ease-out) 0.6s both; }

/* ── Motion: one-time sibling stagger on grids (JS-enhanced; visible without JS) ──
   Children are pre-hidden ONLY once .mk-js is armed (no-JS users always see
   content), so they animate from hidden — never visible→hidden→visible. */
.mk-js [data-stagger]:not(.is-in) > * { opacity: 0; }
.mk-js [data-stagger].is-in > * { animation: mk-rise 0.6s var(--mk-ease-out) both; }
.mk-js [data-stagger].is-in > *:nth-child(2) { animation-delay: 60ms; }
.mk-js [data-stagger].is-in > *:nth-child(3) { animation-delay: 120ms; }
.mk-js [data-stagger].is-in > *:nth-child(4) { animation-delay: 180ms; }
.mk-js [data-stagger].is-in > *:nth-child(5) { animation-delay: 240ms; }
.mk-js [data-stagger].is-in > *:nth-child(6) { animation-delay: 300ms; }
.mk-js [data-stagger].is-in > *:nth-child(7) { animation-delay: 360ms; }

/* ── Buttons & links ── */
.mk-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  padding: 14px 28px; border-radius: 10px; border: none; cursor: pointer;
  background: var(--mk-green); color: #fff;
  font-size: 15px; font-weight: 600; text-decoration: none;
  font-family: inherit;
  transition: background-color 0.18s ease, transform 0.18s ease;
}
.mk-btn:hover { background: var(--mk-green-deep); }
.mk-btn:active { transform: scale(0.98); }
.mk-btn svg { transition: transform 0.2s var(--mk-ease-out); }
.mk-btn:hover svg { transform: translateX(3px); }
.mk-btn:disabled { background: #7AAD9A; cursor: default; }
.mk-btn--ghost {
  background: transparent; color: var(--mk-green);
  border: 1px solid var(--mk-green-border);
}
.mk-btn--ghost:hover { background: var(--mk-green-soft); }
.mk-navlink {
  font-size: 14px; font-weight: 500; color: #4A4A45;
  text-decoration: none; padding: 6px 12px; border-radius: 6px;
  display: inline-block;
  transition: color 0.15s, background-color 0.15s;
}
.mk-navlink:hover { color: var(--mk-ink); background: #F1EFE8; }
.mk a:focus-visible, .mk button:focus-visible {
  outline: 2px solid var(--mk-green); outline-offset: 2px; border-radius: 6px;
}
.mk input:focus-visible, .mk textarea:focus-visible, .mk select:focus-visible {
  outline: none; border-color: var(--mk-green);
  box-shadow: 0 0 0 3px rgba(46, 85, 69, 0.14);
}

/* ── Badge ── */
.mk-badge {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px; border-radius: 20px;
  background: var(--mk-green-soft); border: 1px dashed var(--mk-green-border);
  font-size: 12.5px; font-weight: 600; color: var(--mk-green); letter-spacing: 0.02em;
}
.mk-badge::before {
  content: ''; width: 7px; height: 7px; border-radius: 50%;
  background: var(--mk-green); flex-shrink: 0;
}

/* ── Forms ── */
.mk-label { display: block; font-size: 13px; font-weight: 500; color: #5A5A55; margin-bottom: 6px; }
.mk-input {
  width: 100%; padding: 11px 14px; border-radius: 8px;
  border: 1px solid #DEDCD2; background: var(--mk-bg);
  font-size: 14px; color: var(--mk-ink); font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.mk-input::placeholder { color: var(--mk-faint); } /* ≥4.5:1 — WCAG treats placeholder as text */

/* ── Cards ── */
.mk-card {
  padding: 26px; border-radius: 14px;
  background: var(--mk-bg); border: 1px solid var(--mk-line-soft);
  transition: border-color 0.18s, box-shadow 0.18s;
}
.mk-card:hover { border-color: var(--mk-green-border); box-shadow: 0 6px 24px rgba(20, 20, 15, 0.06); }
.mk-icon-tile {
  width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
  background: var(--mk-green-soft); color: var(--mk-green);
  display: flex; align-items: center; justify-content: center;
}
.mk-feature-card {
  background: var(--mk-surface); border-radius: 16px;
  border: 1px solid var(--mk-line-soft); overflow: hidden;
  display: flex; flex-direction: column;
  transition: border-color 0.18s, box-shadow 0.18s, transform 0.18s;
}
.mk-feature-card:hover {
  border-color: var(--mk-green-border);
  box-shadow: 0 10px 32px rgba(20, 20, 15, 0.09);
  transform: translateY(-3px);
}
.mk-check-row { display: flex; align-items: flex-start; gap: 10px; }
.mk-check-row svg { flex-shrink: 0; margin-top: 3px; }

/* ── Browser frame ── */
.mk-frame {
  border-radius: 14px; overflow: hidden;
  border: 1px solid var(--mk-line);
  box-shadow: 0 24px 64px -16px rgba(27, 51, 42, 0.22);
  background: #fff;
}
.mk-frame-bar {
  background: #EFEDE5; padding: 10px 16px;
  display: flex; align-items: center; gap: 6px;
  border-bottom: 1px solid var(--mk-line);
}

/* ── Module ticker (slow marquee; wraps statically without JS / reduced motion) ── */
.mk-ticker {
  display: flex; flex-wrap: wrap; justify-content: center;
  column-gap: 26px; row-gap: 10px;
  padding: 26px 12px 0;
}
.mk-ticker span {
  font-size: 11.5px; font-weight: 650; letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--mk-faint);
  display: inline-flex; align-items: center; gap: 26px;
  white-space: nowrap;
}
.mk-ticker span + span::before {
  content: ''; width: 4px; height: 4px; border-radius: 50%;
  background: var(--mk-green-border); display: inline-block;
}
@keyframes mk-marquee { to { transform: translateX(-50%); } }
.mk-js .mk-ticker {
  flex-wrap: nowrap; justify-content: flex-start; overflow: hidden;
  mask-image: linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent);
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent);
}
.mk-js .mk-ticker-track {
  display: flex; column-gap: 26px; width: max-content;
  animation: mk-marquee 38s linear infinite;
}
.mk-js .mk-ticker:hover .mk-ticker-track { animation-play-state: paused; }
.mk-ticker-track { display: contents; }
/* the duplicate set only exists for the seamless loop */
.mk-ticker [aria-hidden="true"] { display: none; }
.mk-js .mk-ticker [aria-hidden="true"] { display: inline-flex; }

/* ── Grids ── */
.mk-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.mk-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
.mk-split { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
.mk-split--start { align-items: start; }
@media (max-width: 980px) {
  .mk-grid-3 { grid-template-columns: repeat(2, 1fr); }
  .mk-split { grid-template-columns: 1fr; gap: 40px; }
  .mk-split > .mk-split-media--lead { order: -1; }
}
@media (max-width: 620px) {
  .mk-grid-3, .mk-grid-2 { grid-template-columns: 1fr; }
  .mk-section { padding: 72px 20px; }
  .mk-form-grid { grid-template-columns: 1fr !important; }
}

/* ── Nav ── */
@media (max-width: 768px) {
  .mk-desktop-nav { display: none !important; }
  .mk-mobile-btn { display: flex !important; min-width: 44px; min-height: 44px; align-items: center; justify-content: center; }
}
@media (min-width: 769px) {
  .mk-mobile-menu { display: none !important; }
}

/* ── Dark band ── */
.mk-dark { color: #E9EFEA; }
.mk-dark .mk-display { color: #F5F4EC; }
.mk-dark .mk-display em { color: #9FC3B2; }
.mk-dark .mk-eyebrow { color: #9FC3B2; }
.mk-dark .mk-eyebrow::before { border-color: #9FC3B2; }
.mk-dark p { color: rgba(233, 239, 234, 0.78); }

/* ── Problem ledger (icon rows, stitch dividers — not cards) ── */
.mk-ledger { display: grid; grid-template-columns: 1fr 1fr; column-gap: 56px; }
.mk-ledger-row {
  display: flex; align-items: flex-start; gap: 16px;
  padding: 24px 0; border-bottom: 1px dashed var(--mk-line);
}
@media (max-width: 720px) {
  .mk-ledger { grid-template-columns: 1fr; }
}

/* ── Ruled proof row (dashed column rules — not cards) ── */
.mk-ruled { display: grid; grid-template-columns: repeat(4, 1fr); }
.mk-ruled > div { padding: 4px 28px; border-left: 1px dashed var(--mk-line); }
.mk-ruled > div:first-child { border-left: none; padding-left: 0; }
@media (max-width: 980px) {
  .mk-ruled { grid-template-columns: 1fr 1fr; row-gap: 28px; }
  .mk-ruled > div:nth-child(odd) { border-left: none; padding-left: 0; }
}
@media (max-width: 560px) {
  .mk-ruled { grid-template-columns: 1fr; }
  .mk-ruled > div { border-left: none; padding: 18px 0 0; border-top: 1px dashed var(--mk-line); }
  .mk-ruled > div:first-child { border-top: none; padding-top: 0; }
}

/* ── Footer links ── */
.mk-footlink {
  display: block; font-size: 13px; color: rgba(255,255,255,0.55);
  text-decoration: none; margin-bottom: 10px; transition: color 0.15s;
}
.mk-footlink:hover, .mk-footlink:focus-visible { color: rgba(255,255,255,0.92); }

/* ── Motion ── */
@media (prefers-reduced-motion: no-preference) {
  html:has(.mk) { scroll-behavior: smooth; }
}
@media (prefers-reduced-motion: reduce) {
  .mk *, .mk *::before, .mk *::after {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
  /* staggered content is never hidden for reduced-motion users */
  .mk-js [data-stagger]:not(.is-in) > * { opacity: 1 !important; }
  /* marquee reverts to the static wrapped strip */
  .mk-js .mk-ticker {
    flex-wrap: wrap; justify-content: center; overflow: visible;
    mask-image: none; -webkit-mask-image: none;
  }
  .mk-js .mk-ticker-track { display: contents; animation: none !important; }
  .mk-js .mk-ticker [aria-hidden="true"] { display: none; }
}
`
// ─── Inline SVG icons (lucide-style, 1.5 stroke) ─────────────────────────────
export function Icon({ d, size = 20 }: { d: ReactNode; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {d}
    </svg>
  )
}

export const ICONS = {
  sheet: <><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18M9 4v16"/></>,
  folder: <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.7-.9L9.2 3.9A2 2 0 0 0 7.5 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>,
  checkSquare: <><rect x="3" y="3" width="18" height="18" rx="3"/><path d="m8.5 12 2.5 2.5 5-5"/></>,
  scatter: <><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="7" r="2.5"/><circle cx="8" cy="18" r="2.5"/><circle cx="17" cy="17" r="2.5"/><path d="M8.2 7.6l7.5-.9M7 8.4 7.6 15.5M15 16l-4.7 1.3" opacity="0.45"/></>,
  coins: <><circle cx="8" cy="8" r="5.5"/><path d="M16.5 9.4a5.5 5.5 0 1 1-7.1 7.1"/><path d="M6.5 8h3M8 6.5v3"/></>,
  unlink: <><path d="M9 15l6-6"/><path d="M11 5.9 12.7 4.2a4 4 0 0 1 5.6 5.6L16.6 11.5"/><path d="M13 18.1l-1.7 1.7a4 4 0 0 1-5.6-5.6L7.4 12.5"/><path d="M4 4l2 2M20 20l-2-2" opacity="0.5"/></>,
  store: <><path d="M4 9 5.3 4.5A2 2 0 0 1 7.2 3h9.6a2 2 0 0 1 1.9 1.5L20 9"/><path d="M4 9h16v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9Z"/><path d="M9 14h6"/></>,
  chart: <><path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M8 16v-5M13 16V8M18 16v-8"/></>,
  users: <><circle cx="9" cy="8" r="3.5"/><path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5"/><path d="M16.5 4.8a3.5 3.5 0 0 1 0 6.4M18.5 14.9c1.9.8 3 2.4 3 4.6"/></>,
  star: <path d="m12 3 2.7 5.6 6.1.8-4.5 4.3 1.1 6-5.4-2.9L6.6 19.7l1.1-6L3.2 9.4l6.1-.8L12 3Z"/>,
  message: <path d="M21 12a8 8 0 0 1-8 8H4l2.2-3.3A8 8 0 1 1 21 12Z"/>,
  arrow: <path d="M4 12h16m-6-6 6 6-6 6"/>,
  check: <path d="m4.5 12.5 5 5 10-11"/>,
  shirt: <><path d="M9 4 12 6l3-2 5 3-2 4-2-1v10H8V10l-2 1-2-4 5-3Z"/></>,
  layers: <><path d="m12 3 9 5-9 5-9-5 9-5Z"/><path d="m3 13 9 5 9-5" opacity="0.6"/></>,
  box: <><path d="M3 8.5 12 4l9 4.5v7L12 20l-9-4.5v-7Z"/><path d="m3 8.5 9 4.5 9-4.5M12 13v7"/></>,
  pen: <><path d="m4 20 1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1Z"/><path d="m13 7 4 4"/></>,
  book: <><path d="M4 5a2 2 0 0 1 2-2h14v16H6a2 2 0 0 0-2 2V5Z"/><path d="M4 19a2 2 0 0 1 2-2h14"/></>,
  gear: <><circle cx="12" cy="12" r="3.2"/><path d="M12 2.8v2.6M12 18.6v2.6M21.2 12h-2.6M5.4 12H2.8M18.5 5.5l-1.8 1.8M7.3 16.7l-1.8 1.8M18.5 18.5l-1.8-1.8M7.3 7.3 5.5 5.5"/></>,
  search: <><circle cx="11" cy="11" r="6.5"/><path d="m20 20-3.8-3.8"/></>,
  camera: <><path d="M4 8h3l2-2.5h6L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"/><circle cx="12" cy="13.5" r="3.5"/></>,
  home: <><path d="m3 11 9-7 9 7v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8Z"/><path d="M9 21v-7h6v7"/></>,

}

export function CheckRow({ children, dark }: { children: ReactNode; dark?: boolean }) {
  return (
    <div className="mk-check-row">
      <svg width="15" height="15" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M2.5 7l3 3L11.5 4" stroke={dark ? 'var(--mk-green-pale)' : 'var(--mk-green)'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span style={{ fontSize: 14.5, color: dark ? 'rgba(233,239,234,0.85)' : '#2A2A25', lineHeight: 1.55 }}>{children}</span>
    </div>
  )
}

// ─── NavBar ──────────────────────────────────────────────────────────────────
export function NavBar() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const navLinks = [
    { label: 'Product',  href: '/#product'  },
    { label: 'Features', href: '/features'  },
    // Pricing hidden during beta — see SHOW_PRICING
    ...(SHOW_PRICING ? [{ label: 'Pricing', href: '/#pricing' }] : []),
    { label: 'FAQ',      href: '/#faq'      },
  ]

  return (
    <header
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 50,
        backgroundColor: scrolled ? 'rgba(250,249,246,0.94)' : 'rgba(250,249,246,0.98)',
        borderBottom: scrolled ? '1px dashed var(--mk-line)' : '1px dashed transparent',
        backdropFilter: 'blur(12px)',
        transition: 'border-color 0.2s, background-color 0.2s',
      }}
    >
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>

          {/* Logo */}
          <Link href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo-text-green.png" alt="OBI-Hub" style={{ height: 28, width: 'auto' }} />
          </Link>

          {/* Desktop nav */}
          <nav style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="mk-desktop-nav">
            {navLinks.map(link => (
              <a key={link.href} href={link.href} className="mk-navlink">{link.label}</a>
            ))}
            <Link href="/login" className="mk-navlink">Sign In</Link>
            <Link href="/#beta" className="mk-btn" style={{ padding: '8px 18px', fontSize: 14, marginLeft: 8, borderRadius: 8 }}>
              Apply for beta
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="mk-mobile-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: 'var(--mk-ink)', display: 'none' }}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              {menuOpen ? (
                <>
                  <line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="16" y1="4" x2="4" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </>
              ) : (
                <>
                  <line x1="3" y1="6" x2="17" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="3" y1="14" x2="17" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </>
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="mk-mobile-menu" style={{ borderTop: '1px dashed var(--mk-line)', paddingBottom: 16 }}>
            {[...navLinks, { label: 'Sign In', href: '/login' }].map(link => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                style={{
                  display: 'block', padding: '11px 4px', fontSize: 15,
                  fontWeight: 500, color: 'var(--mk-ink)', textDecoration: 'none',
                  borderBottom: '1px solid var(--mk-line-soft)',
                }}
              >
                {link.label}
              </a>
            ))}
            <a
              href="#beta"
              onClick={() => setMenuOpen(false)}
              className="mk-btn"
              style={{ marginTop: 14, padding: '11px 20px', fontSize: 14, borderRadius: 8 }}
            >
              Apply for beta
            </a>
          </div>
        )}
      </div>
    </header>
  )
}


// ─── Footer (shared across marketing pages) ──────────────────────────────────
export function MarketingFooter() {
  return (
      <footer style={{ backgroundColor: 'var(--mk-ink)', padding: '48px 24px 32px' }}>
        <div style={{ maxWidth: 1160, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 32, marginBottom: 40 }}>
            {/* Brand */}
            <div style={{ maxWidth: 280 }}>
              <div style={{ marginBottom: 14 }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/logo-text-white.png" alt="OBI-Hub" style={{ height: 26, width: 'auto' }} />
              </div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.62)', lineHeight: 1.65, margin: 0 }}>
                OBI stands for One Brand Interface — the operating system for small clothing brands.
                Built by a real brand, for real brands.
              </p>
            </div>

            {/* Links */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.48)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Product</div>
                {[
                  { label: 'Product',  href: '/#product' },
                  { label: 'Features', href: '/features' },
                  // Pricing hidden during beta — see SHOW_PRICING
                  ...(SHOW_PRICING ? [{ label: 'Pricing', href: '/#pricing' }] : []),
                  { label: 'Beta',     href: '/#beta'    },
                  { label: 'FAQ',      href: '/#faq'     },
                ].map(item => (
                  <a key={item.label} href={item.href} className="mk-footlink">
                    {item.label}
                  </a>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.48)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Account</div>
                <a href="/login" className="mk-footlink">
                  Sign In
                </a>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.48)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Legal</div>
                {([
                  { label: 'Privacy Policy',   href: '/legal/privacy' },
                  { label: 'Terms of Service', href: '/legal/terms' },
                  { label: 'Acceptable Use',   href: '/legal/acceptable-use' },
                  { label: 'Refund Policy',    href: '/legal/refunds' },
                  { label: 'Contact',          href: 'mailto:support@obi-hub.app' },
                ] as { label: string; href: string }[]).map(item => (
                  <a key={item.label} href={item.href} className="mk-footlink">
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div style={{
            borderTop: '1px dashed rgba(255,255,255,0.12)',
            paddingTop: 24, display: 'flex',
            flexWrap: 'wrap', justifyContent: 'space-between', gap: 12,
          }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.48)', margin: 0 }}>
              © {new Date().getFullYear()} Obi-Hub. All rights reserved.
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.48)', margin: 0 }}>
              Currently in private beta · Launching soon
            </p>
          </div>
        </div>
      </footer>
  )
}

// ─── Scroll-reveal hook ──────────────────────────────────────────────────────
// Progressive enhancement: without JS everything is fully visible; with JS,
// `.mk-js` arms the seam-draw + stagger animations and the observer fires
// each `[data-reveal]` element once on entry.
export function useMarketingReveal(rootRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const root = rootRef.current
    if (!root || !('IntersectionObserver' in window)) return
    root.classList.add('mk-js')
    const io = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in')
            io.unobserve(e.target)
          }
        }
      },
      // fire just as the element clears the fold so the reveal reads as an
      // entrance rather than a late blink over already-visible content
      { threshold: 0.05, rootMargin: '0px 0px -6% 0px' }
    )
    root.querySelectorAll('[data-reveal]').forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [rootRef])
}
